import React from 'react';
import {
  PieChart as RechartsPieChart, Pie, Cell,
  Tooltip, ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PieChart } from 'lucide-react';
import TabTip from './ui/TabTip';
import { INITIAL_RC_DIM } from '../utils/rechartsDefaults';

const TOOLTIP_STYLE = { backgroundColor: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: '8px' };

/**
 * Reusable pie chart card component to eliminate repetition in allocation view.
 */
function AllocationPieCard({ title, description, data, totalValue, chartColors, formatCurrency, useItemColors = false, helpContent }) {
  return (
    <Card className="portfolio-card">
      <CardHeader>
        <CardTitle className="text-base sm:text-xl font-serif flex items-center">
          <span>{title}</span>
          {helpContent && <TabTip showIcon content={helpContent} />}
        </CardTitle>
        <CardDescription className="body-text">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64 px-2">
          <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_RC_DIM}>
            <RechartsPieChart margin={{ top: 8, right: 40, bottom: 8, left: 40 }}>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ cx, cy, midAngle, outerRadius, percent, fill }) => {
                  if (percent < 0.05) return null;
                  const RADIAN = Math.PI / 180;
                  const radius = outerRadius + 14;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  return (
                    <text
                      x={x}
                      y={y}
                      fill={fill}
                      fontSize={11}
                      textAnchor={x > cx ? 'start' : 'end'}
                      dominantBaseline="central"
                    >
                      {`${(percent * 100).toFixed(1)}%`}
                    </text>
                  );
                }}
                outerRadius={80}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.name}`}
                    fill={useItemColors ? entry.color : chartColors[index % chartColors.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value) => [formatCurrency(value), 'Value']}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
        <div className={`space-y-${useItemColors ? '2' : '1'} mt-4 ${!useItemColors ? 'max-h-48 overflow-y-auto' : ''}`}>
          {data.map((item, index) => {
            const percentage = totalValue > 0 ? ((item.value / totalValue) * 100).toFixed(1) : '0.0';
            return (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div
                    className={`${useItemColors ? 'w-4 h-4' : 'w-3 h-3'} rounded`}
                    style={{ backgroundColor: useItemColors ? item.color : chartColors[index % chartColors.length] }}
                  />
                  <span className={`text-sm text-slate-700 ${useItemColors ? 'font-medium' : ''}`} data-sensitive="identifier">{item.name}</span>
                </div>
                {useItemColors ? (
                  <div className="flex items-center space-x-3">
                    <span className="text-sm font-mono text-slate-900 font-bold">
                      <span data-sensitive="monetary">{formatCurrency(item.value)}</span>
                    </span>
                    <span className="text-sm text-slate-500">({percentage}%)</span>
                  </div>
                ) : (
                  <span className="text-sm text-slate-500">({percentage}%)</span>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * AllocationTab - Extracted from Dashboard.js
 * Shows portfolio allocation charts (Ownership, Asset Class, Liquidity, Country, Duration)
 */
function CounterpartyConcentrationCard({
  counterpartyChartData, totalValue, chartColors, formatCurrency,
  isConcentrated, topThirdPartyCounterparty, topConcentrationPct,
  unspecifiedPct, counterpartyHelp,
}) {
  if (!counterpartyChartData || counterpartyChartData.length === 0) return null;
  return (
    <Card className="portfolio-card" data-testid="counterparty-concentration-card">
      <CardHeader>
        <CardTitle className="text-base sm:text-xl font-serif flex items-center justify-between gap-2">
          <span className="flex items-center">
            <span>By Counterparty</span>
            <TabTip showIcon content={counterpartyHelp} />
          </span>
          {isConcentrated && (
            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-300 text-xs font-medium" data-testid="counterparty-concentration-warning-badge">
              Concentration ≥ 25%
            </Badge>
          )}
        </CardTitle>
        <CardDescription className="body-text">
          Who holds or issues each asset — banks, brokers, custodians, fund managers, issuers
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-64 px-2" data-sensitive="chart">
          <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_RC_DIM}>
            <RechartsPieChart margin={{ top: 8, right: 40, bottom: 8, left: 40 }}>
              <Pie
                data={counterpartyChartData}
                cx="50%" cy="50%" labelLine={false}
                label={({ cx, cy, midAngle, outerRadius, percent, fill }) => {
                  if (percent < 0.05) return null;
                  const RADIAN = Math.PI / 180;
                  const radius = outerRadius + 14;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  return (<text x={x} y={y} fill={fill} fontSize={11} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">{`${(percent * 100).toFixed(1)}%`}</text>);
                }}
                outerRadius={80} dataKey="value"
              >
                {counterpartyChartData.map((entry, index) => {
                  const isTopAndConcentrated = isConcentrated && entry.name === topThirdPartyCounterparty?.name;
                  const isSelfCustodied = entry.isThirdParty === false && !entry.isUnspecified;
                  const isUnspecified = entry.isUnspecified === true;
                  return (
                    <Cell
                      key={`cell-cp-${entry.name}`}
                      fill={isTopAndConcentrated ? '#F59E0B' : isUnspecified ? '#CBD5E1' : isSelfCustodied ? '#94A3B8' : chartColors[index % chartColors.length]}
                      stroke={isTopAndConcentrated ? '#B45309' : undefined}
                      strokeWidth={isTopAndConcentrated ? 2 : 0}
                    />
                  );
                })}
              </Pie>
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value, _name, props) => {
                  const cnt = props?.payload?.count;
                  return [`${formatCurrency(value)}${cnt ? ` · ${cnt} holding${cnt !== 1 ? 's' : ''}` : ''}`, 'Value'];
                }}
              />
            </RechartsPieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-1 mt-4 max-h-48 overflow-y-auto">
          {counterpartyChartData.map((item, index) => {
            const percentage = totalValue > 0 ? ((item.value / totalValue) * 100).toFixed(1) : '0.0';
            const isTop = isConcentrated && item.name === topThirdPartyCounterparty?.name;
            const isSelfCustodied = item.isThirdParty === false && !item.isUnspecified;
            const isUnspecified = item.isUnspecified === true;
            return (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: isTop ? '#F59E0B' : isUnspecified ? '#CBD5E1' : isSelfCustodied ? '#94A3B8' : chartColors[index % chartColors.length] }} />
                  <span className={`text-sm ${isTop ? 'text-amber-900 font-medium' : isUnspecified ? 'text-slate-400 italic' : isSelfCustodied ? 'text-slate-500 italic' : 'text-slate-700'}`} data-sensitive="identifier">{item.name}</span>
                </div>
                <span className={`text-sm ${isTop ? 'text-amber-900 font-medium' : 'text-slate-500'}`}>({percentage}%)</span>
              </div>
            );
          })}
        </div>
        {isConcentrated && topThirdPartyCounterparty && (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="counterparty-concentration-warning-note">
            <span className="font-semibold">{topThirdPartyCounterparty.name}</span> represents{' '}
            <span className="font-semibold">{topConcentrationPct.toFixed(1)}%</span> of your portfolio.
            Concentration in a single third-party counterparty above 25% materially raises your single-point-of-failure risk.
          </div>
        )}
        {unspecifiedPct > 0 && (
          <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid="counterparty-unspecified-note">
            <span className="font-semibold">{unspecifiedPct.toFixed(1)}%</span> of your portfolio has no counterparty recorded.
            Add the institution, issuer or fund manager on those holdings to surface their true concentration.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const AllocationTab = ({
  portfolioSummary,
  ownershipChartData,
  typeChartData,
  liquidityChartData,
  countryChartData,
  durationChartData,
  currencyChartData,
  counterpartyChartData,
  topCounterparty,
  topThirdPartyCounterparty,
  topConcentrationPct,
  isConcentrated,
  unspecifiedPct,
  chartColors,
  formatCurrency,
}) => {
  if (!portfolioSummary || portfolioSummary.total_investments <= 0) {
    return (
      <Card className="portfolio-card">
        <CardContent className="p-12 text-center">
          <PieChart className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h3 className="text-xl font-serif font-bold text-slate-900 mb-2">No Data Available</h3>
          <p className="body-text">
            Add investments using the button above to see your portfolio allocation
          </p>
        </CardContent>
      </Card>
    );
  }

  const totalValue = portfolioSummary.current_value;

  const liquidityHelp = (
    <div className="space-y-2" data-testid="liquidity-help-content">
      <p className="font-semibold">How liquidity is classified</p>
      <p className="text-slate-200 text-xs italic">Based on time-to-cash and exit friction (IFRS 9 / CFA convention).</p>
      <div>
        <p className="text-emerald-300 font-medium">High</p>
        <p className="text-slate-200">Bank balances, Treasury bills, mutual funds, ETFs, quoted stocks and crypto — convertible to cash within days, deep markets.</p>
      </div>
      <div>
        <p className="text-amber-300 font-medium">Medium</p>
        <p className="text-slate-200">Government bonds, corporate bonds and gold — typically days to weeks; depth and spread vary by issuer or form.</p>
      </div>
      <div>
        <p className="text-rose-300 font-medium">Low</p>
        <p className="text-slate-200">Fixed deposits, private equity, real estate, convertible notes and SAFEs — locked or restricted; exit costs are material.</p>
      </div>
      <p className="text-slate-300 text-xs">Fixed deposits sit in Low because early withdrawal carries penalties — even if the maturity itself is short.</p>
    </div>
  );

  const currencyHelp = (
    <div className="space-y-2" data-testid="currency-help-content">
      <p className="font-semibold">Allocation by Currency</p>
      <p className="text-slate-200">Each slice is one currency you hold, sized by its converted value in your display currency. So a £100 slice and a $100 slice are sized fairly side by side.</p>
      <p className="text-slate-300 text-xs">Native amounts are shown on the &ldquo;Total Investments by Currency&rdquo; card in the Overview tab.</p>
    </div>
  );

  const durationHelp = (
    <div className="space-y-2" data-testid="duration-help-content">
      <p className="font-semibold">How time horizon is classified</p>
      <p className="text-slate-200 text-xs italic">If a maturity date is set, that always wins. Otherwise we use the asset type as the default below.</p>
      <div>
        <p className="text-blue-300 font-medium">Short-term (under 1 year)</p>
        <p className="text-slate-200">Bank balances, Treasury bills and fixed deposits — plus anything maturing within 12 months.</p>
      </div>
      <div>
        <p className="text-amber-300 font-medium">Medium-term (1–5 years)</p>
        <p className="text-slate-200">Mutual funds, ETFs, quoted stocks and crypto — and anything maturing in 1–5 years.</p>
      </div>
      <div>
        <p className="text-violet-300 font-medium">Long-term (over 5 years)</p>
        <p className="text-slate-200">Bonds (without a maturity date), real estate, private equity, gold, convertibles and SAFEs — plus anything maturing beyond 5 years.</p>
      </div>
      <p className="text-slate-300 text-xs">For bonds, T-bills and fixed deposits the maturity date you enter takes precedence over the asset-type default.</p>
    </div>
  );

  const counterpartyHelp = (
    <div className="space-y-2" data-testid="counterparty-help-content">
      <p className="font-semibold">Concentration by Counterparty</p>
      <p className="text-slate-200">The single entity that holds or issues each asset — bank, broker, custodian, fund manager or issuer. A large slice means a single point of failure.</p>
      <div>
        <p className="text-emerald-300 font-medium">How each asset is grouped</p>
        <p className="text-slate-200">Bonds &amp; Treasury bills → <span className="italic">issuer name</span>. Mutual funds &amp; Private equity → <span className="italic">fund manager</span>. Everything else (stocks, FDs, crypto) → <span className="italic">institution</span>.</p>
      </div>
      <div>
        <p className="text-amber-300 font-medium">Self-custodied / Direct &amp; Unspecified</p>
        <p className="text-slate-200">Real estate and gold are physical (no counterparty risk). Investments with no institution/issuer recorded show up as <span className="italic">Unspecified</span>. Both are <span className="italic">excluded</span> from the 25% concentration warning.</p>
      </div>
      <p className="text-slate-300 text-xs">The amber highlight only fires when a single named third-party counterparty exceeds 25% of the total portfolio.</p>
    </div>
  );

  return (
    <>
      <div>
        <h2 className="text-lg sm:text-2xl font-serif font-bold text-slate-900 mb-2">Portfolio Allocation</h2>
        <p className="body-text">
          Comprehensive breakdown of your investment distribution across multiple dimensions
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        <AllocationPieCard
          title="By Ownership"
          description="Direct vs Indirect vs Related portfolios"
          data={ownershipChartData}
          totalValue={totalValue}
          chartColors={chartColors}
          formatCurrency={formatCurrency}
          useItemColors={true}
        />

        <AllocationPieCard
          title="By Asset Class"
          description="Distribution across investment types"
          data={typeChartData}
          totalValue={totalValue}
          chartColors={chartColors}
          formatCurrency={formatCurrency}
        />

        <AllocationPieCard
          title="By Liquidity"
          description="How quickly assets can be converted to cash"
          data={liquidityChartData}
          totalValue={totalValue}
          chartColors={chartColors}
          formatCurrency={formatCurrency}
          useItemColors={true}
          helpContent={liquidityHelp}
        />

        <AllocationPieCard
          title="By Country"
          description="Geographic distribution of investments"
          data={countryChartData}
          totalValue={totalValue}
          chartColors={chartColors}
          formatCurrency={formatCurrency}
        />

        <AllocationPieCard
          title="By Duration"
          description="Time horizon classification of investments"
          data={durationChartData}
          totalValue={totalValue}
          chartColors={chartColors}
          formatCurrency={formatCurrency}
          useItemColors={true}
          helpContent={durationHelp}
        />

        {currencyChartData && currencyChartData.length > 0 && (
          <AllocationPieCard
            title="By Currency"
            description="Investment distribution across currencies (native amounts)"
            data={currencyChartData}
            totalValue={totalValue}
            chartColors={chartColors}
            formatCurrency={formatCurrency}
            helpContent={currencyHelp}
          />
        )}

        {counterpartyChartData && counterpartyChartData.length > 0 && (
          <CounterpartyConcentrationCard
            counterpartyChartData={counterpartyChartData}
            totalValue={totalValue}
            chartColors={chartColors}
            formatCurrency={formatCurrency}
            isConcentrated={isConcentrated}
            topThirdPartyCounterparty={topThirdPartyCounterparty}
            topConcentrationPct={topConcentrationPct}
            unspecifiedPct={unspecifiedPct}
            counterpartyHelp={counterpartyHelp}
          />
        )}
      </div>
    </>
  );
};

export default AllocationTab;
